'use strict';
import Marionette from 'backbone.marionette';
import _ from 'underscore';

import { BindedView } from 'marionette.binding';
import Modernizr from 'modernizr';

import DocumentCollection from '../collections/documents';
import Document from '../models/document';
import DialogService from '../dialog_service';

/**
 * Upload Service can be called to upload files for you
 */
class UploadService {
  /**
   * Prompt the user to upload a file. The callback will be called
   * with the new Document model if a document was successfully
   * uploaded. If the dialog is dismissed, nothing happens.
   * @param {function} callback The function callback
   * @returns {undefined}
   */
  static uploadFile(callback) {
    let service = new UploadService();
    service.doUploadFile(callback);
  }

  /**
   * Upload a file. This is called by uploadFile and should be treated as an
   * internal method.
   * @param {function} callback The function callback on file upload completion
   * @returns {undefined}
   */
  doUploadFile(callback) {
    if (!Modernizr.filereader && !Modernizr.xhr2) {
      window.open('/joinos/documents/upload/');
      return;
    }

    const uploadView = new AttachmentUploadView({
      model: new Document()
    });
    const dialog = DialogService.show(uploadView);

    dialog.on('close', () => {
      dialog.close();
    });
    uploadView.on('finish', model => {
      dialog.close();
      callback(model);
    });
  }
}

const ImageTypes = ['png', 'jpg', 'jpeg'];
const CautionTypes = [
  'doc',
  'docx',
  'xls',
  'xlsx',
  'odt',
  'ppt',
  'pptx',
  'pub',
  'rtf',
  'ods'
];

const AttachmentUploadView = BindedView.extend({
  template: require('../templates/attachments/new_dialog.html'),

  ui: {
    pdfWarning: '.pdfWarning',
    picker: '.upload',
    title: '#doc_title',
    description: '#doc_description',
    upload: '.uploadButton',
    button: '.button',
    bigImageWarning: '.bigImageWarning',
    tooBig: '.tooBig'
  },

  bindings: {
    'value @ui.title': 'name',
    'value @ui.description': 'description'
  },

  triggers: {
    'click @ui.upload': 'save',
    'change @ui.picker': 'changeFile'
  },

  onChangeFile() {
    const ONE_MB = 1024 * 1024;

    if (!this.ui.picker.get(0)['files']) return;

    const file = this.ui.picker.get(0).files[0];
    const ext = file.name.substr(file.name.lastIndexOf('.') + 1);
    if (CautionTypes.indexOf(ext) != -1) {
      this.ui.pdfWarning.show();
    } else {
      this.ui.pdfWarning.hide();
    }
    if (ImageTypes.indexOf(ext) != -1 && file.size > ONE_MB) {
      this.ui.bigImageWarning.show();
    } else {
      this.ui.bigImageWarning.hide();
    }

    this.model.set('file', file);
  },

  onSave() {
    this.ui.button.prop('disabled', 'disabled');
    this.ui.upload.text('Uploading...');
    const self = this;
    this.model
      .save()
      .done(function () {
        self.trigger('finish', self.model);
      })
      .fail(function () {
        self.ui.button.removeAttr('disabled');
        self.ui.upload.text('Upload');
        window.messages.add('Failed to upload document', 'error');
      });
  },

  onShow() {
    this.ui.pdfWarning.hide();
    this.ui.bigImageWarning.hide();
    this.ui.tooBig.hide();
  }
});

const AttachmentItemView = Marionette.LayoutView.extend({
  template: require('../templates/attachments/item.html'),

  templateHelpers() {
    const url = this.model.get('url');
    const extension = url.substr(url.lastIndexOf('.') + 1);

    return {
      extension: extension.toUpperCase(),
      icon: `/static/icons/documents/${this._getIcon(extension)}`,
      caution: CautionTypes.indexOf(extension) >= 0,
      mode: this.options.mode
    };
  },

  ui: {
    remove: '.removeButton',
    select: '.selectButton'
  },

  triggers: {
    'click @ui.remove': 'remove',
    'click @ui.select': 'select'
  },

  /**
   * Return the icon path.
   * @param {string} extension The file extension.
   * @returns {string} The icon path.
   */
  _getIcon(extension) {
    if (ImageTypes.indexOf(extension) >= 0) {
      return 'image.svg';
    } else if (extension == 'pdf') {
      return 'pdf.svg';
    } else if (_.contains(['doc', 'docx', 'odt'], extension)) {
      return 'word.svg';
    }
    return 'other.svg';
  }
});

const NoAttachmentsView = Marionette.LayoutView.extend({
  template() {
    return 'No attachments to display';
  }
});

const AttachmentListView = Marionette.CollectionView.extend({
  childView: AttachmentItemView,
  emptyView: NoAttachmentsView,
  collectionEvents: {
    sync: 'render'
  },

  childViewOptions() {
    return {
      mode: this.options.mode
    };
  },

  childEvents: {
    remove: 'onChildRemove',
    select: 'onChildSelect'
  },

  onChildRemove(childView) {
    if (
      confirm('Are you sure? (Note: this does not delete it from the system)')
    ) {
      this.collection.remove(childView.model);
    }
  },

  onChildSelect(childView) {
    this.trigger('finish', childView.model);
  }
});

const AttachmentSelectView = Marionette.LayoutView.extend({
  template: require('../templates/attachments/existing_dialog.html'),

  regions: {
    list: '.list-hook'
  },

  onRender() {
    const collection = new DocumentCollection();
    let list = new AttachmentListView({
      collection,
      mode: 'select'
    });
    this.listenTo(list, 'finish', this.finish);
    this.showChildView('list', list);
    collection.fetch();
  },

  finish(model) {
    this.trigger('finish', model);
  }
});

const AttachmentView = Marionette.LayoutView.extend({
  template: require('../templates/attachments/layout.html'),

  regions: {
    modal: '.modal',
    list: '.list'
  },

  ui: {
    newFile: '.newFile',
    existingFile: '.existingFile'
  },

  triggers: {
    'click @ui.newFile': 'newFile',
    'click @ui.existingFile': 'existingFile'
  },

  onNewFile() {
    UploadService.uploadFile(model => {
      this.collection.add(model);
    });
  },

  onExistingFile() {
    const c = new AttachmentSelectView();
    this.m = DialogService.show(c);
    this.listenTo(this.m, 'close', this.closeModal);
    this.listenTo(c, 'finish', this.finish);
  },

  closeModal() {
    this.m.close();
  },

  finish(m) {
    this.closeModal();
    this.collection.add(m);
    this.list.render();
  },

  onShow() {
    this.list = new AttachmentListView({
      collection: this.collection,
      mode: 'list'
    });
    this.showChildView('list', this.list);
  }
});

export { ImageTypes, UploadService, AttachmentView };
export default AttachmentView;



// WEBPACK FOOTER //
// ./base/views/attachments.js